import React, { useState, useEffect } from 'react';
import {
    Button,
    TextField,
    Typography,
    CssBaseline,
    ThemeProvider,
    createTheme,
    Container,
    Paper,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';

const API_URL = process.env.REACT_APP_API_URL || 'https://aqa.wtdev.dev';

const Login = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const checkAuthToken = () => {
        const token = Cookies.get('auth_token');
        if (token) navigate('/dashboard');
    };

    useEffect(() => {
        checkAuthToken();

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkAuthToken();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [navigate]);

    const handleLogin = async () => {
        try {
            const response = await fetch(`${API_URL}/api/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password }),
                credentials: 'include',
            });

            if (response.ok) {
                Cookies.set('auth_token', 'true', { expires: 2 });
                navigate('/dashboard');
            } else {
                setError('Incorrect credentials, login failed');
            }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (err) {
            setError('Login error. Try again later.');
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && username && password) {
            handleLogin();
        }
    };

    return (
        <Container component="main" maxWidth="xs">
            <Paper elevation={6} sx={{ p: 4, mt: 10, bgcolor: '#1a1d24', color: '#f1f1f1' }}>
                <Typography variant="h5" gutterBottom align="center">
                    Dashboard Login
                </Typography>
                <TextField
                    margin="normal"
                    fullWidth
                    label="Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    onKeyDown={handleKeyDown}
                    sx={{
                        input: { color: '#f1f1f1' },
                        '& label': { color: '#f1f1f1' },
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#555' },
                            '&:hover fieldset': { borderColor: '#888' },
                            '&.Mui-focused fieldset': { borderColor: '#f1f1f1' },
                        },
                    }}
                />
                <TextField
                    margin="normal"
                    fullWidth
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={handleKeyDown}
                    sx={{
                        input: { color: '#f1f1f1' },
                        '& label': { color: '#f1f1f1' },
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#555' },
                            '&:hover fieldset': { borderColor: '#888' },
                            '&.Mui-focused fieldset': { borderColor: '#f1f1f1' },
                        },
                    }}
                />
                {error && (
                    <Typography sx={{ color: '#f28b82', mt: 2 }}>{error}</Typography>
                )}
                <Button
                    variant="contained"
                    fullWidth
                    sx={{ mt: 3 }}
                    onClick={handleLogin}
                >
                    Login
                </Button>
            </Paper>
        </Container>
    );
};

const darkTheme = createTheme({
    palette: {
        mode: 'dark',
        background: { default: '#0f1117', paper: '#1a1d24' },
        text: { primary: '#f1f1f1' },
    },
});

const WrappedLogin = () => (
    <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <Login />
    </ThemeProvider>
);

export default WrappedLogin;
